import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  Pic,
  PopIn,
  RoundCounter,
  ShakeWrap,
  gameShell,
  seededShuffle,
  useCompleteOnce,
  useLetterAccent,
  useWrongFlash,
} from "./engineUtils";

/**
 * Memory flip: cards flip in 3D; find every picture pair. A mismatch flips
 * back after a beat; matched pairs stay up and glow. Completes when all
 * pairs are found.
 */
export default function MemoryPairGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent, deep } = useLetterAccent(letter);
  const cards = useMemo(() => {
    const bank = seededShuffle(config.items || config.pairs || [], 14).slice(0, config.pairCount || 4);
    const doubled = bank.flatMap((it, i) => [
      { ...it, cardId: `${it.word}-a-${i}`, pairId: it.word },
      { ...it, cardId: `${it.word}-b-${i}`, pairId: it.word },
    ]);
    return seededShuffle(doubled, 28);
  }, [config]);

  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(() => new Set());
  const [justMatched, setJustMatched] = useState(null);
  const [lock, setLock] = useState(false);
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);
  const pairTotal = cards.length / 2;

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (pairTotal > 0 && matched.size >= pairTotal) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      const t = setTimeout(finish, reduceMotion ? 0 : 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [matched, pairTotal, finish, reduceMotion, sfx]);

  const onTap = (card) => {
    if (lock || matched.has(card.pairId) || flipped.find((c) => c.cardId === card.cardId)) return;
    sfx?.("pageturn", { volume: 0.45 });
    speak?.(card.word, { interrupt: true });
    const next = [...flipped, card];
    setFlipped(next);
    if (next.length < 2) return;
    setLock(true);
    const [a, b] = next;
    if (a.pairId === b.pairId) {
      timers.current.push(setTimeout(() => {
        sfx?.("sparkle", { volume: 0.6 });
        setMatched((s) => new Set(s).add(a.pairId));
        setJustMatched(a.pairId);
        setFlipped([]);
        setLock(false);
      }, reduceMotion ? 120 : 420));
    } else {
      timers.current.push(setTimeout(() => {
        flash(a.cardId);
        flash(b.cardId);
        onWrong?.();
      }, reduceMotion ? 100 : 500));
      timers.current.push(setTimeout(() => {
        setFlipped([]);
        setLock(false);
      }, reduceMotion ? 350 : 1000));
    }
  };

  // Cards grow to 1.7 tiles so eight of them fill ~60% of a desktop arena,
  // and shrink to whatever the arena's width/height allow (never scroll).
  const cols = cards.length <= 8 ? 4 : cards.length <= 12 ? 4 : 5;
  const rows = Math.ceil(cards.length / cols);
  const gap = compact ? 8 : 12;
  const cardSize = `min(calc(var(--tile) * 1.7), calc((var(--arena-w) - ${compact ? 8 : 24}px - ${(cols - 1) * gap}px) / ${cols}), calc((var(--arena-h) - ${compact ? 44 : 24}px - ${(rows - 1) * gap}px) / ${rows}))`;

  return (
    <div style={{ ...gameShell.root, gap: compact ? 8 : 12 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} reduceMotion={reduceMotion} />
      <RoundCounter done={matched.size} total={pairTotal} accent={accent} />
      <div
        style={{
          flex: 1, minHeight: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${cardSize})`,
          gridAutoRows: cardSize,
          gap, justifyContent: "center", alignContent: "center",
          perspective: 1200,
          paddingTop: compact ? 30 : 0,
        }}
        aria-label="Memory cards"
      >
        {cards.map((c, i) => {
          const isMatched = matched.has(c.pairId);
          const show = isMatched || flipped.some((f) => f.cardId === c.cardId);
          return (
            <PopIn key={c.cardId} index={i} reduceMotion={reduceMotion} style={{ width: "100%", height: "100%" }}>
              <ShakeWrap shake={wrongId === c.cardId} reduceMotion={reduceMotion} style={{ borderRadius: "calc(var(--tile) * 0.3)", width: "100%", height: "100%" }}>
                <button
                  type="button"
                  className="learn-piece"
                  aria-label={show ? c.word : "Hidden card"}
                  aria-pressed={show}
                  onClick={() => onTap(c)}
                  style={{
                    background: "transparent", border: "none", padding: 0, width: "100%", height: "100%",
                    cursor: isMatched ? "default" : "pointer", borderRadius: "calc(var(--tile) * 0.3)", perspective: 900,
                  }}
                >
                  <motion.div
                    animate={{ rotateY: show ? 180 : 0, scale: justMatched === c.pairId && isMatched && !reduceMotion ? [1, 1.1, 1] : 1 }}
                    transition={reduceMotion ? { duration: 0 } : { rotateY: { type: "spring", stiffness: 260, damping: 22 }, scale: { duration: 0.5 } }}
                    onAnimationComplete={() => { if (justMatched === c.pairId) setJustMatched(null); }}
                    style={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d" }}
                  >
                    {/* Back */}
                    <div
                      style={{
                        position: "absolute", inset: 0, borderRadius: "calc(var(--tile) * 0.3)",
                        background: `linear-gradient(145deg, ${accent}, ${deep})`,
                        border: "3px solid rgba(255,255,255,0.85)",
                        boxShadow: `0 4px 0 ${deep}, ${LEARN.shadow.card}`,
                        display: "grid", placeItems: "center",
                        backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
                        overflow: "hidden",
                      }}
                    >
                      <span aria-hidden="true" style={{ position: "absolute", inset: 0, opacity: 0.16, backgroundImage: "radial-gradient(circle at 30% 30%, #fff 0 8%, transparent 9%), radial-gradient(circle at 70% 70%, #fff 0 8%, transparent 9%)", backgroundSize: "34% 34%" }} />
                      <span aria-hidden="true" style={{ fontFamily: LEARN.font, fontWeight: 700, fontSize: `calc(${cardSize} * 0.46)`, color: "#fff", textShadow: `0 3px 0 ${deep}`, lineHeight: 1 }}>
                        {String(letter || "?").toUpperCase()}
                      </span>
                    </div>
                    {/* Face */}
                    <div
                      style={{
                        position: "absolute", inset: 0, borderRadius: "calc(var(--tile) * 0.3)",
                        background: "#fff",
                        border: `3px solid ${isMatched ? LEARN.color.mint : LEARN.color.line}`,
                        boxShadow: isMatched ? `0 0 0 4px ${LEARN.color.mint}66, 0 10px 24px ${LEARN.color.mint}55` : LEARN.shadow.card,
                        display: "grid", placeItems: "center",
                        transform: "rotateY(180deg)",
                        backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
                        transition: "box-shadow 300ms ease, border-color 300ms ease",
                        animation: isMatched && !reduceMotion ? "learn-glow 1.8s ease-in-out infinite" : undefined,
                      }}
                    >
                      <Pic letter={letter} assetName={c.assetName} alt={c.word} size="78%" />
                    </div>
                  </motion.div>
                </button>
              </ShakeWrap>
            </PopIn>
          );
        })}
      </div>
    </div>
  );
}
